import { dataAPI } from "../api"
import {
    ODataGetArrayResponse,
    ODataGetResourceResponse,
} from "../models/api/data"
import { Type, TypeModel } from "../models/treeSelector/models"

import { parseGroupValueData } from "../treeSelector/treeSelector"
import { SelectableOptionWithGroup } from "../treeSelector/types"
import { filterQueryMapper, orderQueryMapper } from "../treeSelector/utils"
import { ItemFieldToFieldDefinitionType } from "./utils"

const getSelectableUrl = <T extends Type>(
    itemId: number,
    fieldDefinitionId: string,
    fieldId: string,
    type: T,
    fieldDefinition: string,
): string => {
    switch (type) {
        case "TF.OptionItemField":
        case "TF.OptionsItemField":
            return `FieldDefinitions('${fieldDefinitionId}')/${fieldDefinition}/AvailableValues`
        case "TF.AccountCodeItemField":
            return "AccountCodes"
        case "TF.ActivityItemField":
            return "Activities"
        case "TF.ApplicationItemField":
            return "Applications"
        case "TF.ApplicationVersionItemField":
            return "ApplicationVersions"
        case "TF.BillTypeItemField":
            return "BillTypes"
        case "TF.DisplayTypeItemField":
            return "DisplayTypes"
        case "TF.ImpactItemField":
            return "Impacts"
        case "TF.ProbabilityItemField":
            return "Probabilities"
        case "TF.UserItemField":
        case "TF.UsersItemField":
            return `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')/TF.ItemsItemField/Query/SelectableUsers()`
        case "TF.UserGroupItemField":
        case "TF.UserGroupsItemField":
            return `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')/TF.ItemsItemField/Query/SelectableUserGroups()`
        case "TF.TagsItemField":
            return `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')/TF.ItemsItemField/Query/SelectableTags()`
        default:
            return ""
    }
}

export async function getSelectableFilterGroupOptions<T extends Type>(
    itemId: number,
    fieldDefinitionId: string,
    fieldId: string,
    filter = "",
    skip = 0,
    top = 20,
    type: T,
): Promise<SelectableOptionWithGroup> {
    const filterQuery = filterQueryMapper(filter, type, false)
    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, unknown> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $orderby: orderBy ? orderBy : undefined,
        $top: top,
        $skip: skip,
        $count: true,
    }

    const fieldDefinition = ItemFieldToFieldDefinitionType[type] ?? ""

    const url = getSelectableUrl(
        itemId,
        fieldDefinitionId,
        fieldId,
        type,
        fieldDefinition,
    )

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(url, parameters)
        .then((response) => {
            return {
                data: parseGroupValueData(response, type),
                totalChildrenCount: response["@count"] ?? 0,
            }
        })
}

export async function getSelectableFilterOptions<T extends Type>(
    itemId: number,
    fieldDefinitionId: string,
    fieldId: string,
    filter = "",
    skip = 0,
    top = 20,
    type: T,
): Promise<SelectableOptionWithGroup> {
    const filterQuery = filterQueryMapper(filter, type, false)
    const orderBy = orderQueryMapper(type)

    const parameters: Record<string, unknown> = {
        $filter: !!filterQuery ? `(${filterQuery})` : undefined,
        $orderby: orderBy ? orderBy : undefined,
        $top: top,
        $skip: skip,
        $count: true,
    }

    const fieldDefinition = ItemFieldToFieldDefinitionType[type] ?? ""

    const url = getSelectableUrl(
        itemId,
        fieldDefinitionId,
        fieldId,
        type,
        fieldDefinition,
    )

    return dataAPI
        .getJSON<
            | ODataGetArrayResponse<TypeModel[T]>
            | ODataGetResourceResponse<TypeModel[T]>
        >(url, parameters)
        .then((response) => {
            return {
                data: url ? parseGroupValueData(response, type) : [],
                totalChildrenCount: url ? response["@count"] ?? 0 : 0,
            }
        })
}
